import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { RefreshCw, WifiOff } from 'lucide-react';
import React from 'react';

interface NetworkErrorPageProps {
    onRetry?: () => void;
}

export const NetworkErrorPage: React.FC<NetworkErrorPageProps> = ({ onRetry }) => {
    return (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background p-4 text-center animate-in fade-in zoom-in duration-300">
            <div className="max-w-md space-y-8">
                {/* Animated Icon Container */}
                <div className="relative mx-auto flex h-32 w-32 items-center justify-center rounded-full bg-muted/30">
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{
                            repeat: Infinity,
                            repeatType: "reverse",
                            duration: 2,
                            ease: "easeInOut"
                        }}
                        className="absolute inset-0 rounded-full bg-destructive/10"
                    />
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1.1, opacity: 0.5 }}
                        transition={{
                            repeat: Infinity,
                            repeatType: "reverse",
                            duration: 2,
                            delay: 0.5,
                            ease: "easeInOut"
                        }}
                        className="absolute inset-0 rounded-full bg-destructive/10"
                    />
                    <WifiOff className="h-16 w-16 text-destructive" />
                </div>

                {/* Text Content */}
                <div className="space-y-4">
                    <h1 className="text-4xl font-bold tracking-tight text-foreground">
                        No Internet Connection
                    </h1>
                    <p className="text-lg text-muted-foreground">
                        It looks like you're offline. Please check your internet connection and try again.
                    </p>
                </div>

                {/* Action Button */}
                <div className="flex justify-center pt-4">
                    <Button
                        size="lg"
                        onClick={() => window.location.reload()}
                        className="group gap-2 min-w-[150px] font-semibold"
                    >
                        <RefreshCw className="h-4 w-4 transition-transform group-hover:rotate-180" />
                        Retry Connection
                    </Button>
                </div>
            </div>

            {/* Decorative background elements */}
            <div className="absolute top-10 left-10 h-24 w-24 rounded-full bg-primary/5 blur-3xl" />
            <div className="absolute bottom-10 right-10 h-32 w-32 rounded-full bg-destructive/5 blur-3xl" />
        </div>
    );
};
